/**
 * @file 数据概览页（Dashboard）
 *
 * 该文件定义了 CRM 系统的首页数据概览组件 `CrmDashboardPage`。
 * 页面采用左右两栏布局（1:3）：
 * - 左列（1/4，收窄）：统计卡片（总客户数、待处理任务）+ 待跟进提醒列表 + 近 3 天邮件发送状态环形图
 * - 右列（3/4）：电子日历（`<crm-email-calendar>`，macOS 风格月视图，展示「日历」标签邮件）
 *
 * 主要功能：
 * - 展示两张统计卡片（总客户数、待处理任务）
 * - 待跟进提醒列表（支持标记完成、逾期高亮）
 * - 近 3 天邮件发送状态环形图（SVG stroke-dasharray 实现的 donut chart）
 * - 月视图电子日历（切月调接口，点击邮件条目跳转邮件详情）
 *
 * 数据来源：`dashboardApi.overview()` 返回的 `DashboardOverview` 类型，
 * 以及 `followUpApi.list()` 返回的跟进提醒列表；日历数据由子组件自行加载。
 *
 * 设计决策：
 * - 图表使用原生 SVG / CSS 绘制，避免引入重量级图表依赖
 * - 统计卡片图标内联为 SVG 方法，保持组件自包含
 * - 页面加载时并行请求概览数据和跟进提醒以提高首屏速度
 */

import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { dashboardApi, followUpApi } from '../api.js';
import type { DashboardOverview, FollowUpReminder } from '../types.js';
import { toast } from '../components/toast.js';
import { isNetworkError } from '../utils/index.js';
import '../components/email-calendar.js';
import '../components/loading-overlay.js';

/**
 * 统计卡片数据结构
 * @property label - 卡片标签文本，如「总客户数」
 * @property value - 卡片展示的数值字符串，已格式化（如 toLocaleString 结果）
 * @property icon - SVG 图标的 Lit TemplateResult，通过方法动态生成
 * @property color - 图标颜色对应的 Tailwind 文字色类名，如 'text-primary'
 */
interface StatCard {
  label: string;
  value: string;
  icon: ReturnType<typeof html>;
  color: string;
}

/**
 * 数据概览页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-dashboard-page>` 自定义元素。
 * 负责加载并渲染 CRM 系统首页的统计数据、图表和跟进提醒列表。
 *
 * 状态字段分组：
 * - `stats` — 两张统计卡片数据（总客户数、待处理任务）
 * - `emailStats` — 邮件发送状态环形图各分段数据
 * - `followUps` — 待跟进提醒列表
 */
@customElement('crm-dashboard-page')
export class CrmDashboardPage extends BaseElement {
  /** 统计卡片数组，初始值用占位符「-」填充，加载后替换为实际数据 */
  @state()
  private stats: StatCard[] = [
    { label: '总客户数', value: '-', icon: this.customerIcon(), color: 'text-primary' },
    { label: '待处理任务', value: '-', icon: this.taskIcon(), color: 'text-warning' },
  ];

  /** 邮件发送状态环形图数据，每项含标签、数值和颜色 */
  @state()
  private emailStats: { label: string; value: number; color: string }[] = [];

  /** 待跟进提醒列表，从 followUpApi 获取未完成的提醒 */
  @state()
  private followUps: FollowUpReminder[] = [];
  /** 概览统计接口请求状态，覆盖统计卡片和邮件图表 */
  @state()
  private overviewLoading = false;
  /** 跟进提醒接口请求状态，仅覆盖待跟进卡片 */
  @state()
  private followUpsLoading = false;

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 调用父类 `connectedCallback` 后，并行发起两个数据请求：
   * - `fetchOverview()` 获取统计概览数据
   * - `fetchFollowUps()` 获取待跟进提醒列表
   *
   * 使用 `Promise.all` 并行执行以减少首屏等待时间。
   */
  async connectedCallback(): Promise<void> {
    super.connectedCallback();
    await Promise.all([this.fetchOverview(), this.fetchFollowUps()]);
  }

  /**
   * 获取未完成的跟进提醒列表
   *
   * 调用 `followUpApi.list({ is_completed: 0 })` 获取所有未完成的提醒，
   * 成功时写入 `this.followUps`，失败时清空列表为空数组（静默处理不弹 toast）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async fetchFollowUps(): Promise<void> {
    this.followUpsLoading = true;
    try {
      const res = await followUpApi.list({ is_completed: 0 });
      this.followUps = res.data;
    } catch {
      // 加载失败时静默清空，保持空状态
      this.followUps = [];
    } finally {
      this.followUpsLoading = false;
    }
  }

  /**
   * 标记某条跟进提醒为已完成
   *
   * 调用 `followUpApi.update(id, { is_completed: 1 })` 将指定提醒标记为已完成，
   * 成功后从本地列表中移除该条记录（乐观 UI 更新）。
   * 失败时显示错误 toast（过滤网络错误不重复弹窗）。
   *
   * @param id - 待完成的跟进提醒 ID
   * @returns 无返回值（Promise<void>）
   */
  private async handleCompleteReminder(id: number): Promise<void> {
    try {
      await followUpApi.update(id, { is_completed: 1 });
      // 从本地列表中移除已完成的提醒，触发列表重新渲染
      this.followUps = this.followUps.filter(r => r.id !== id);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '操作失败';
      // 网络错误由全局拦截器处理，此处不重复弹窗
      if (!isNetworkError(msg)) toast.error(msg);
    }
  }

  /**
   * 获取数据概览并更新统计卡片与环形图状态
   *
   * 调用 `dashboardApi.overview()` 获取后端聚合数据，然后更新：
   * - 统计卡片（用 toLocaleString 格式化数值）
   * - 邮件发送状态环形图分段
   *
   * 失败时保留默认空状态（卡片显示「-」，图表显示「暂无数据」）。
   *
   * @returns 无返回值（Promise<void>）
   */
  private async fetchOverview(): Promise<void> {
    this.overviewLoading = true;
    try {
      const data: DashboardOverview = await dashboardApi.overview();
      // 更新统计卡片，数值通过 toLocaleString 添加千分位分隔符
      this.stats = [
        { label: '总客户数', value: data.customer_count.toLocaleString(), icon: this.customerIcon(), color: 'text-primary' },
        { label: '待处理任务', value: data.pending_task_count.toLocaleString(), icon: this.taskIcon(), color: 'text-warning' },
      ];
      // 映射邮件发送状态环形图数据
      this.emailStats = data.send_status.map(s => ({
        label: s.label,
        value: s.value,
        color: s.color,
      }));
    } catch {
      // 加载失败时保留默认空状态，不弹 toast（仪表盘容错降级）
    } finally {
      this.overviewLoading = false;
    }
  }

  /**
   * 客户图标 SVG
   * @returns 包含人物图标的 Lit TemplateResult，用于统计卡片
   */
  private customerIcon(): ReturnType<typeof html> {
    return html`<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-3-3.87"/><path d="M7 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/><path d="M18.5 7.5a3.2 3.2 0 0 0 0-3"/><path d="M5.5 7.5a3.2 3.2 0 0 1 0-3"/></svg>`;
  }

  /**
   * 任务图标 SVG
   * @returns 包含编辑/笔图标的 Lit TemplateResult，用于统计卡片
   */
  private taskIcon(): ReturnType<typeof html> {
    return html`<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>`;
  }

  /**
   * 渲染数据概览页面
   *
   * 页面布局为左右两栏（1:3）：
   * 1. 左列（1/4）：统计卡片（纵向堆叠）→ 待跟进提醒 → 发送状态环形图
   * 2. 右列（3/4）：电子日历（`<crm-email-calendar>`，随网格拉伸与左列等高）
   *
   * 环形图计算说明：
   * - totalEmail 用于环形图中心总数显示
   * - accumulatedOffset 在环形图渲染时累加各分段的 stroke 偏移量
   *
   * @returns Lit TemplateResult
   */
  render() {
    // 环形图中心展示的邮件总数
    const totalEmail = this.emailStats.reduce((sum, s) => sum + s.value, 0);
    // 环形图 SVG 圆参数：半径 40，计算周长用于 stroke-dasharray 计算
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    // 累积偏移量，在环形图 map 中逐段递增以实现分段效果
    let accumulatedOffset = 0;

    return html`
      <!-- Page Header -->
      <div class="mb-6">
        <h1 class="m-0 font-semibold text-2xl text-text">数据概览</h1>
        <p class="m-0 mt-1 text-sm text-text-secondary">欢迎回来，以下是您的业务数据摘要</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-5 mb-5">
        <!-- 左列（1/4，收窄）：统计卡片 + 跟进提醒 + 发送状态 -->
        <div class="flex flex-col gap-5">
          <!-- 统计卡片（纵向堆叠） -->
          ${this.stats.map(stat => html`
            <div class="relative bg-surface rounded-md p-5 shadow-md hover:shadow-lg transition-shadow">
              <crm-loading-overlay .active=${this.overviewLoading}></crm-loading-overlay>
              <div class="flex items-center justify-center mb-4">
                <div class="w-10 h-10 rounded-md bg-bg flex items-center justify-center ${stat.color}">
                  ${stat.icon}
                </div>
              </div>
              <div class="text-2xl font-semibold text-text text-center">${stat.value}</div>
              <div class="text-sm text-text-muted mt-1 text-center">${stat.label}</div>
            </div>
          `)}

          <!-- Follow-up Reminders -->
          <div class="relative bg-surface rounded-md p-6 shadow-md">
            <crm-loading-overlay .active=${this.followUpsLoading}></crm-loading-overlay>
            <h3 class="m-0 mb-5 font-semibold text-text flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 text-warning"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              待跟进提醒 (${this.followUps.length})
            </h3>
            ${this.followUps.length > 0 ? html`
              <div class="space-y-2 max-h-64 overflow-y-auto">
                ${this.followUps.map(r => {
                  // 计算是否逾期：当前时间戳（秒）与提醒时间比较
                  const now = Math.floor(Date.now() / 1000);
                  const overdue = r.remind_at < now;
                  return html`
                    <div class="flex items-center gap-3 p-2 rounded hover:bg-bg/50 transition-colors ${overdue ? 'bg-error/5' : ''}">
                      <span class="text-xs font-mono ${overdue ? 'text-error font-semibold' : 'text-text-muted'} w-16 shrink-0">
                        ${new Date(r.remind_at * 1000).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })}
                      </span>
                      <span class="text-sm font-medium text-text truncate flex-1 min-w-0">
                        ${r.customer_name || r.company_name || '(无名称)'}
                        ${overdue ? html`<span class="text-error text-xs ml-1">逾期</span>` : ''}
                      </span>
                      <button class="text-xs font-medium text-success hover:text-success-hover px-2 py-1 rounded transition-colors cursor-pointer bg-transparent border-none shrink-0"
                        @click=${() => this.handleCompleteReminder(r.id)}>完成</button>
                    </div>
                  `;
                })}
              </div>
            ` : html`<p class="text-sm text-text-muted text-center py-8">暂无待跟进提醒</p>`}
          </div>

          <!-- Email Send Status (Donut Chart) -->
          <div class="relative bg-surface rounded-md p-6 shadow-md">
            <crm-loading-overlay .active=${this.overviewLoading}></crm-loading-overlay>
            <h3 class="m-0 mb-6 font-semibold text-text">近3天邮件发送状态</h3>
            <div class="flex flex-col items-center">
              ${this.emailStats.length > 0 ? html`
              <div class="relative w-36 h-36">
                <svg viewBox="0 0 100 100" class="w-full h-full -rotate-90">
                  ${this.emailStats.map(stat => {
                    // 计算当前分段占周长的比例
                    const pct = stat.value / totalEmail;
                    // dash 长度 = 比例 × 周长，gap = 周长 - dash 长度
                    const dashLength = pct * circumference;
                    const dashGap = circumference - dashLength;
                    // 偏移量取负值使各分段顺时针排列，累加已渲染的长度
                    const offset = -accumulatedOffset;
                    accumulatedOffset += dashLength;
                    return html`
                      <circle cx="50" cy="50" r="${radius}" fill="none" stroke="${stat.color}" stroke-width="12"
                        stroke-dasharray="${dashLength} ${dashGap}"
                        stroke-dashoffset="${offset}"
                        stroke-linecap="round"
                        style="transition: all 0.5s ease;"/>
                    `;
                  })}
                </svg>
                <div class="absolute inset-0 flex flex-col items-center justify-center">
                  <span class="text-2xl font-semibold text-text">${totalEmail}</span>
                  <span class="text-xs text-text-muted">总计</span>
                </div>
              </div>
              <div class="mt-4 space-y-2 w-full">
                ${this.emailStats.map(stat => html`
                  <div class="flex items-center justify-between text-sm">
                    <div class="flex items-center gap-2">
                      <span class="w-2.5 h-2.5 rounded-full" style="background: ${stat.color};"></span>
                      <span class="text-text-muted">${stat.label}</span>
                    </div>
                    <span class="font-medium text-text">${stat.value}</span>
                  </div>
                `)}
              </div>
              ` : html`<p class="text-sm text-text-muted text-center py-4">暂无数据</p>`}
            </div>
          </div>
        </div>

        <!-- 右列（3/4）：电子日历（macOS 风格月视图，展示「日历」标签邮件） -->
        <crm-email-calendar class="lg:col-span-3"></crm-email-calendar>
      </div>

      `;
  }
}
